import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSavedCards } from "@/hooks/useSavedCards";
import AddPaymentMethodInline from "@/components/payment/AddPaymentMethodInline";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CreditCard, Plus, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BRAND_ICONS: Record<string, string> = {
  visa: "💳 Visa",
  mastercard: "💳 Mastercard",
  amex: "💳 Amex",
  discover: "💳 Discover",
};

export default function SavedCardsSection() {
  const { t } = useTranslation("common");
  const { cards, isLoading, deleteCard, setDefault } = useSavedCards();
  const [showAddDialog, setShowAddDialog] = useState(false);

  // 2d-ii Step 2: the SetupIntent → Stripe Elements → confirmCardSetup → saveCard sequence moved
  // verbatim into <AddPaymentMethodInline> (shared with the activity-join confirm). Opening the
  // dialog mounts it, which runs the same flow; the add-card behaviour is unchanged.
  const handleOpenAdd = () => setShowAddDialog(true);

  const handleDelete = async (id: string) => {
    if (!confirm(t("profile.payment.confirmDeleteCard"))) return;
    try {
      await deleteCard.mutateAsync(id);
      toast.success(t("profile.payment.cardDeleted"));
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefault.mutateAsync(id);
      toast.success(t("profile.payment.defaultChanged"));
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          {t("profile.payment.savedCards")}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={handleOpenAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          {t("profile.payment.addCard")}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : cards.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t("profile.payment.noCards")}
          </p>
        ) : (
          <div className="space-y-2">
            {cards.map((card) => (
              <div key={card.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">
                    {BRAND_ICONS[card.card_brand] ?? `💳 ${card.card_brand}`}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    •••• {card.card_last4}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {String(card.card_exp_month).padStart(2, "0")}/{card.card_exp_year}
                  </span>
                  {card.is_default && (
                    <Badge variant="secondary" className="text-[10px]">
                      {t("profile.payment.default")}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!card.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => handleSetDefault(card.id)} title={t("profile.payment.setDefault")}>
                      <Star className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(card.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Card Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(v) => { if (!v) setShowAddDialog(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("profile.payment.addCardTitle")}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {showAddDialog && (
              <AddPaymentMethodInline
                onSaved={() => setShowAddDialog(false)}
                onCancel={() => setShowAddDialog(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
